import { Component, OnInit, OnDestroy } from '@angular/core';
import { Auth, signInWithPopup, GoogleAuthProvider, OAuthProvider, User } from '@angular/fire/auth';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { Identity } from '../../models/Identity';
import { Subscription } from 'rxjs';
import { PageButtonComponent } from '../buttons/page-button.component';
import { DialogButtonComponent } from '../buttons/dialog-button.component';
import { FontService } from '../../services/font.service';

const translations: Record<string, Record<string, string>> = {
  'delete_account':       { sv: 'Radera konto', en: 'Delete account' },
  'login_to_delete':      { sv: 'Logga in för att radera ditt konto och all tillhörande data.', en: 'Sign in to delete your account and all associated data.' },
  'continue_with_apple':  { sv: 'Fortsätt med Apple', en: 'Continue with Apple' },
  'continue_with_google': { sv: 'Fortsätt med Google', en: 'Continue with Google' },
  'login_same_account':   { sv: 'Logga in med samma konto som du använde när du skapade din profil.', en: 'Sign in with the same account you used when creating your profile.' },
  'login_failed':         { sv: 'Inloggningen misslyckades. Försök igen.', en: 'Sign in failed. Please try again.' },
  'name':                 { sv: 'Namn', en: 'Name' },
  'email':                { sv: 'E-post', en: 'Email' },
  'wrong_account':        { sv: 'Fel konto?', en: 'Wrong account?' },
  'logout_switch':        { sv: 'Logga ut och byt konto', en: 'Sign out and switch account' },
  'delete_profile':       { sv: 'Radera profilen', en: 'Delete profile' },
  'bullet_1':             { sv: 'Profil och alla händelser kommer att raderas.', en: 'Profile and all events will be deleted.' },
  'bullet_2':             { sv: 'Köphistorik kommer inte längre att kopplas till profilen och blir anonym.', en: 'Purchase history will no longer be linked to your profile and will become anonymous.' },
  'bullet_3':             { sv: 'Köphistorik kan inte återställas.', en: 'Purchase history cannot be restored.' },
  'bullet_4':             { sv: 'Alla rabattkoder tas bort.', en: 'All discount codes will be removed.' },
  'bullet_5':             { sv: 'Eventuella presentkort/rabattkort tas bort.', en: 'Any gift cards/loyalty cards will be removed.' },
  'bullet_6':             { sv: 'Det kan ta upp till 30 dagar innan all personlig information är raderad eller anonymiserad.', en: 'It may take up to 30 days for all personal information to be deleted or anonymized.' },
  'delete_profile_button':{ sv: 'RADERA PROFIL', en: 'DELETE PROFILE' },
  'are_you_sure':         { sv: 'Är du säker på att du vill radera profilen?', en: 'Are you sure you want to delete your profile?' },
  'all_data_lost':        { sv: 'All data, inklusive kvitton, rabattkort, rabattkoder etc., kommer att försvinna.', en: 'All data, including receipts, loyalty cards, discount codes etc., will be lost.' },
  'cancel':               { sv: 'Avbryt', en: 'Cancel' },
  'delete':               { sv: 'Radera', en: 'Delete' },
  'deleting':             { sv: 'Raderar...', en: 'Deleting...' },
  'account_deleted':      { sv: 'Kontot raderat', en: 'Account deleted' },
  'account_deleted_info': { sv: 'Ditt konto och all tillhörande data har raderats. Det kan ta upp till 30 dagar innan allt är borttaget.', en: 'Your account and all associated data has been deleted. It may take up to 30 days for everything to be removed.' },
  'something_went_wrong': { sv: 'Något gick fel. Försök igen senare.', en: 'Something went wrong. Please try again later.' },
};

@Component({
  selector: 'app-delete-account',
  standalone: true,
  imports: [
    PageButtonComponent,
    DialogButtonComponent,
  ],
  styles: [`
    :host {
      --theme-body: #f5f5f4;
      --theme-surface: #ffffff;
      --theme-surface2: #f5f5f4;
      --theme-text: #1c1917;
      --theme-text2: #44403c;
      --theme-text3: #a8a29e;
      --theme-primary: #1c1917;
      --theme-contrast: #fafaf9;
      --theme-secondary-button: #1c1917;
      --theme-outline: #1c191733;
      --theme-warning: #dc2626;
      --theme-notification: #1C7ED6;
      --theme-notification-text: #fafaf9;
      --theme-notification2: #E8795D;
      --theme-notification2-text: #fafaf9;
      --surface-80-mix: 80%;
      --theme-radius: inherit;
      --theme-overlay-opacity: 0.3;
      --theme-surfacex: #ffffff;
      display: block;
    }
  `],
  template: `
    <div class="h-[100svh] bg-theme-body overflow-hidden">

      <!-- Top bar (mirrors menu-base) -->
      <div class="fixed top-0 left-0 right-0 max-w-lg mx-auto z-10">
        <div class="p-2">
          <div class="relative bg-theme-surface-80 backdrop-blur-md shadow-sm min-w-32 h-14 flex items-center w-full rounded-2xl p-1">
            <div class="w-24 flex justify-start">
              <div class="w-12 h-12 rounded-full bg-theme-surface flex items-center justify-center shadow-sm">
                <img src="favicon.svg" alt="HeyNow" class="w-7 h-7">
              </div>
            </div>
            <div class="absolute left-1/2 transform -translate-x-1/2">
              <div class="font-medium text-theme-text text-lg">{{ t('delete_account') }}</div>
            </div>
            <div class="absolute right-1 flex items-center">
              <div class="w-12 h-12 bg-theme-surface rounded-full flex items-center justify-center cursor-pointer hover:bg-theme-surface2 transition-colors text-sm font-semibold text-theme-text"
                   (click)="toggleLang()">
                {{ lang === 'sv' ? 'EN' : 'SV' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scrollable content area -->
      <div class="overflow-y-auto h-full">
        <div class="max-w-lg mx-auto flex flex-col pt-20 min-h-screen">
          <div class="flex-1 bg-theme-surface rounded-t-3xl min-h-full p-4 text-theme-text">

            @if (state === 'loading') {
              <div class="flex justify-center py-16">
                <div class="w-10 h-10 border-4 border-theme-text3 border-t-theme-text rounded-full animate-spin"></div>
              </div>
            }

            @if (state === 'login') {
              <div class="pt-4 space-y-6">
                <p class="text-base text-theme-text">
                  {{ t('login_to_delete') }}
                </p>

                <div class="px-4 pt-2 bg-primary rounded">
                  <app-page-button [text]="t('continue_with_apple')" icon="fa-brands fa-apple"
                    (onClick)="signIn('apple')"></app-page-button>
                </div>
                <div class="px-4 pt-2 bg-primary rounded">
                  <app-page-button [text]="t('continue_with_google')" icon="fa-brands fa-google"
                    (onClick)="signIn('google')"></app-page-button>
                </div>

                <div class="text-sm text-theme-text3 px-4">
                  {{ t('login_same_account') }}
                </div>

                @if (error) {
                  <p class="text-red-600 text-sm px-4">{{ error }}</p>
                }
              </div>
            }

            @if (state === 'confirm') {
              <div class="space-y-6 pt-4">
                <!-- Profile card -->
                <div class="bg-theme-surface2 rounded-lg shadow p-6">
                  <div class="flex items-center">
                    @if (user?.photoURL) {
                      <img [src]="user!.photoURL" alt="Profilbild" class="w-24 h-24 rounded-full">
                    }
                    <div class="ml-8 text-theme-text">
                      @if (user?.displayName) {
                        <div class="mb-2">
                          <span class="font-semibold">{{ t('name') }}:</span> {{ user!.displayName }}
                        </div>
                      }
                      @if (user?.email) {
                        <div>
                          <span class="font-semibold">{{ t('email') }}:</span> {{ user!.email }}
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <div class="text-center text-base text-theme-text3">
                  {{ t('wrong_account') }} <a class="underline cursor-pointer" (click)="logout()">{{ t('logout_switch') }}</a>
                </div>

                @if (!showConfirm) {
                  <div class="space-y-6">
                    <h2 class="text-2xl font-bold text-theme-text">{{ t('delete_profile') }}</h2>
                    <ul class="list-disc pl-6 text-theme-text">
                      <li>{{ t('bullet_1') }}</li>
                      <li>{{ t('bullet_2') }}</li>
                      <li>{{ t('bullet_3') }}</li>
                      <li>{{ t('bullet_4') }}</li>
                      <li>{{ t('bullet_5') }}</li>
                      <li>{{ t('bullet_6') }}</li>
                    </ul>
                    <div class="mt-8">
                      <button
                        class="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg shadow hover:bg-red-700 transition w-full md:w-auto"
                        (click)="showConfirm = true">
                        {{ t('delete_profile_button') }}
                      </button>
                    </div>
                  </div>
                }

                @if (showConfirm) {
                  <div class="bg-theme-surface rounded-2xl shadow-xl max-w-2xl w-full">
                    <div class="p-6 max-w-full">
                      <h2 class="text-xl font-bold mb-4">{{ t('are_you_sure') }}</h2>
                      <p>{{ t('all_data_lost') }}</p>
                      <div class="mt-4 flex gap-2 justify-end">
                        <app-dialog-button
                          [text]="t('cancel')"
                          (onClick)="showConfirm = false">
                        </app-dialog-button>
                        <app-dialog-button
                          [text]="deleting ? t('deleting') : t('delete')"
                          (onClick)="deleteAccount()"
                          [disabled]="deleting"
                          variant="warning">
                        </app-dialog-button>
                      </div>
                    </div>
                  </div>
                }

                @if (error) {
                  <p class="mt-4 text-red-600 text-sm">{{ error }}</p>
                }
              </div>
            }

            @if (state === 'done') {
              <div class="text-center py-8">
                <div class="text-green-600 text-4xl mb-4">
                  <i class="fa-solid fa-circle-check"></i>
                </div>
                <h1 class="text-2xl font-bold text-theme-text mb-2">{{ t('account_deleted') }}</h1>
                <p class="text-theme-text3">{{ t('account_deleted_info') }}</p>
              </div>
            }

          </div>
        </div>
      </div>

    </div>
  `,
})
export class DeleteAccountComponent implements OnInit, OnDestroy {
  state: 'loading' | 'login' | 'confirm' | 'done' = 'loading';
  user: User | null = null;
  showConfirm = false;
  deleting = false;
  error: string | null = null;
  lang = 'sv';

  private unsubscribeAuth?: () => void;
  private wipeSub?: Subscription;

  constructor(
    private firebaseAuth: Auth,
    private api: AuthorizedApiService,
    private fontService: FontService,
  ) {}

  ngOnInit(): void {
    const stored = localStorage.getItem('collinsLang');
    this.lang = stored === 'en' ? 'en' : 'sv';
    this.fontService.loadFont('outfit');
    this.unsubscribeAuth = this.firebaseAuth.onAuthStateChanged(user => {
      this.user = user;
      if (user && !user.isAnonymous) {
        this.setupApiIdentity();
        this.state = 'confirm';
      } else {
        this.state = 'login';
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeAuth?.();
    this.wipeSub?.unsubscribe();
  }

  t(key: string): string {
    return translations[key]?.[this.lang] || translations[key]?.['sv'] || key;
  }

  toggleLang(): void {
    this.lang = this.lang === 'sv' ? 'en' : 'sv';
  }

  private setupApiIdentity(): void {
    const identity = new Identity();
    identity.venueId = '';
    this.api.setIdentity(identity);
  }

  async signIn(provider: 'apple' | 'google'): Promise<void> {
    this.error = null;
    try {
      const authProvider = provider === 'apple'
        ? new OAuthProvider('apple.com')
        : new GoogleAuthProvider();
      await signInWithPopup(this.firebaseAuth, authProvider);
    } catch (e: any) {
      if (e?.code !== 'auth/popup-closed-by-user') {
        this.error = this.t('login_failed');
      }
    }
  }

  async logout(): Promise<void> {
    await this.firebaseAuth.signOut();
  }

  deleteAccount(): void {
    this.deleting = true;
    this.error = null;
    this.wipeSub = this.api.wipeProfile().subscribe({
      next: () => {
        this.firebaseAuth.signOut();
        this.state = 'done';
        this.deleting = false;
      },
      error: () => {
        this.error = this.t('something_went_wrong');
        this.deleting = false;
      }
    });
  }
}
